import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { createUri, Api } from '@api/utils';

import { Query, Mutation } from './utils';
import { type System, type StationSearchResult, SystemUrl } from '@api/system';

enum Tag {
    Systems = 'systems',
    System = 'system',
    Config = 'config',
}

export const systemHooks = createApi({
    reducerPath: 'systemApi',
    tagTypes: Object.values(Tag),
    baseQuery: fetchBaseQuery({
        baseUrl: createUri(Api.Systems, '/system/V1'),
    }),
    endpoints: (build) => ({
        systemById: build.query<System, { token: string; id: string; type?: 'assigned' | 'unassigned' }>({
            query: ({ token, id, type }) => ({
                url: SystemUrl.Station,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: {
                    stationBy: 'stationId',
                    prop: id,
                    type,
                },
            }),
            transformResponse: (data: any[] = []) => data[0],
            providesTags: ({ SerialNum } = {} as System, _, args) => [
                { type: Tag.System, StationID: args.id },
                { type: Tag.System, SerialNum },
            ],
        }),
        systemsBySerialNum: build.query<
            System[],
            { token: string; serialNum: string; type?: 'assigned' | 'unassigned' }
        >({
            query: ({ token, serialNum, type }) => ({
                url: SystemUrl.Station,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: {
                    stationBy: 'serialNum',
                    prop: serialNum,
                    type,
                },
            }),
            providesTags: (_, __, args) => [{ type: Tag.System, SerialNum: args.serialNum }],
        }),
        systemConfig: build.query<
            Record<string, { materials: Record<string, { forceOffset: number }> }> | null,
            { token: string; serialNumber: string }
        >({
            query: ({ token, serialNumber }) => ({
                url: SystemUrl.Config,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: {
                    serialNumber,
                },
            }),
            providesTags: (_, __, args) => [{ type: Tag.Config, SerialNum: args.serialNumber }],
        }),
        systemConfigUpload: build.mutation<
            unknown,
            { token: string; serialNumber: string; config: Record<string, any>; stationID: number }
        >({
            query: ({ token, serialNumber, config, stationID }) => ({
                url: SystemUrl.Config,
                method: 'PUT',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: {
                    forceOffsets: config,
                    serialNumber,
                    stationID,
                },
            }),
            invalidatesTags: (_, __, args) => [{ type: Tag.Config, SerialNum: args.serialNumber }],
        }),
        systems: build.query<
            {
                data: System[];
                total: number;
            },
            {
                token: string;
                pageNum: number;
                pageSize: number;
                type: 'unassigned' | 'assigned';
                status?: string;
                model?: string;
                companyIds?: string;
                resellerIds?: string;
                locationIds?: string;
            }
        >({
            query: ({ token, ...params }) => ({
                url: SystemUrl.Stations,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params,
            }),
            transformResponse: (data: ({ totalStationsInQuery: string } & System)[] = []) => {
                return {
                    data,
                    total: +data[0]?.totalStationsInQuery || 0,
                };
            },
            providesTags: [Tag.Systems],
        }),
        stationSearch: build.query<StationSearchResult[], { token: string; search: string }>({
            query: ({ token, search }) => ({
                url: SystemUrl.StationSearch,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: { search },
            }),
        }),
        System: build.mutation<unknown, { token: string; system: System }>({
            query: ({ token, system }) => ({
                url: SystemUrl.Station,
                method: 'PATCH',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: system,
            }),
            invalidatesTags: (_, __, { system: { SerialNum, StationID } }) => [
                { type: Tag.System, StationID },
                { type: Tag.System, SerialNum },
                Tag.Systems,
            ],
        }),
    }),
});

/** Intermediary Wrapper */
const system = {
    useSystemByIdQuery: Query.withToken(systemHooks.useSystemByIdQuery),
    useSystemsBySerialNumQuery: Query.withToken(systemHooks.useSystemsBySerialNumQuery),
    useSystemsQuery: Query.withToken(systemHooks.useSystemsQuery),
    useSystemMutation: Mutation.withToken(systemHooks.useSystemMutation),
    useSystemConfigMutation: Mutation.withToken(systemHooks.useSystemConfigUploadMutation),
    useSystemConfigQuery: Query.withToken(systemHooks.useSystemConfigQuery),
    useStationSearchQuery: Query.withToken(systemHooks.useStationSearchQuery),
};

export const {
    useSystemByIdQuery,
    useSystemsBySerialNumQuery,
    useSystemsQuery,
    useSystemMutation,
    useSystemConfigMutation,
    useSystemConfigQuery,
    useStationSearchQuery,
} = system;
